import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { backendClient } from '@/lib/backendClient';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

interface ShortsUsage {
  id: string;
  shorts_generated_today: number;
  last_reset_date: string;
  total_shorts_generated: number;
  overage_charges_today: number;
  free_shorts_remaining: number;
}

interface ShortsUsageResult {
  shorts_generated_today: number;
  free_shorts_remaining: number;
  charge_amount: number;
  transaction_id?: string;
  is_overage: boolean;
}

export const usePractitionerShortsUsage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch current shorts usage
  const {
    data: usage,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['practitioner-shorts-usage', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await backendClient
        .rpc('get_practitioner_shorts_usage', { p_practitioner_id: user.id });

      if (error) throw error;
      return data?.[0] as ShortsUsage | null;
    },
    enabled: !!user?.id,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true,
  });

  // Increment shorts usage
  const incrementUsageMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const { data, error } = await backendClient
        .rpc('increment_shorts_usage', { p_practitioner_id: user.id });

      if (error) throw error;
      return data as unknown as ShortsUsageResult;
    },
    onSuccess: (result) => {
      // Invalidate and refetch usage data
      queryClient.invalidateQueries({ queryKey: ['practitioner-shorts-usage'] });
      
      // Show toast for overage charges
      if (result.is_overage && result.charge_amount > 0) {
        toast({
          title: "Overage Charge Applied",
          description: `You've been charged $${result.charge_amount.toFixed(2)} for this short (exceeded 10 free shorts today).`,
          variant: "default",
        });
      }
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to track shorts usage: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  const dailyLimit = 10;
  const overageRate = 0.50;
  const isAtLimit = false; // No hard limit, just charges after 10
  const canGenerate = true; // Always can generate, just with charges

  return {
    usage,
    isLoading,
    error,
    refetch,
    incrementUsage: incrementUsageMutation.mutate,
    isIncrementing: incrementUsageMutation.isPending,
    dailyLimit,
    overageRate,
    isAtLimit,
    canGenerate,
    freeRemaining: usage?.free_shorts_remaining || 0,
    generatedToday: usage?.shorts_generated_today || 0,
    overageChargesTotal: usage?.overage_charges_today || 0,
    totalGenerated: usage?.total_shorts_generated || 0,
  };
};
